import type { IVaultDetail, IVaultStatus } from './types';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Link, useNavigate, useParams } from 'react-router-dom';

import { useEvaultAddon, useNotification } from 'hooks';
import { Blank, PageHead, StatusChip } from 'views/design-system';
import { PERMISSION_SCREEN, useUserRoles } from 'views/routes-children';

import { EvaultAddonToggle } from './addon-toggle';
import { ArchiveModal } from './archive-modal';
import { CreateVaultModal } from './create-vault-modal';
import { DocumentPanel } from './document-panel';
import { isoOf, toRelativeTime, useIsYou } from './derive';
import { useDriveView, useEntitlement } from './use-evault-deck';
import { useEvaultApi } from './use-evault-api';
import { useVaultDetail } from './use-vault-deck';

import 'views/design-system/tokens.scss';
import 'views/design-system/surface.scss';
import './evault-deck.scss';

const NO_WRITE = 'You need write access to eVault.';
const NEEDS_ADDON_TRANSFER = 'Transfers need the eVault add-on.';

/**
 * One vault — `/evault/:id` (§6).
 *
 * Two fixes from §2 land here rather than in a comment. The documents come from
 * `GET /vault-documents?vaultIds=<id>` — the endpoint that exists — where the
 * component upstream called `GET /vaults/:id/documents`, which is routed nowhere
 * in esign.api or the proxy and 404s every time. Its failure rendered as "No
 * documents yet", so a broken fetch and an empty vault were the same screen
 * (§2.6). And a failed `GET /vaults/:id` now has somewhere to go instead of an
 * eternal skeleton (§2.7).
 *
 * Rename and archive are NOT entitlement-gated: the backend's `requireEntitled`
 * covers create and transfer only, so a workspace whose add-on has lapsed can
 * still tidy up the vaults it already owns — locking someone out of renaming
 * their own records would be a paywall the server does not ask for. Write
 * permission does gate them, through the panel's own `PERMISSION_SCREEN.eVault`.
 */
export const VaultDeck = () => {
	const { id = '' } = useParams();
	const navigate = useNavigate();
	const api = useEvaultApi();
	const isYou = useIsYou();
	const { successNotification } = useNotification();
	const { checkUserWritePermission } = useUserRoles();
	const [view, setView] = useDriveView();
	const entitlement = useEntitlement(api);
	/* `EvaultAddonToggle` takes this as a prop — see its own doc comment. */
	const addon = useEvaultAddon();

	const { vault, state, message, refetch, applyVault } = useVaultDetail(api, id);

	const [renameOpen, setRenameOpen] = useState(false);
	const [archiveOpen, setArchiveOpen] = useState(false);

	const canWrite = useMemo(
		() => checkUserWritePermission(PERMISSION_SCREEN.eVault),
		[checkUserWritePermission]
	);

	/* `false` means the server said no; `null` means the probe never answered,
	   and an unanswered probe is not a refusal (see IEntitlementState). */
	const blocked = entitlement.entitled === false;
	const archived = vault?.status === 'archived';

	/* Same two-step unlock as the drive (evault-deck.tsx): `entitlement.refetch`
	   and `refetch`, called directly here, would still be the PRE-remint
	   closures — this callback is fixed at the render before the click. The
	   effect below runs on the NEXT render instead, once the new token is the
	   one these hooks were built from. Read through refs, not as effect deps:
	   `useEntitlement` returns a fresh object every render, so depending on it
	   directly would re-fire this effect on every unrelated re-render for as
	   long as `unlockPending` stays true. */
	const [unlockPending, setUnlockPending] = useState(false);
	const entitlementRef = useRef(entitlement);
	entitlementRef.current = entitlement;
	const refetchRef = useRef(refetch);
	refetchRef.current = refetch;

	const unlock = useCallback(async () => {
		await api.remintToken();
		setUnlockPending(true);
	}, [api]);

	useEffect(() => {
		if (!unlockPending) return;
		void (async () => {
			await entitlementRef.current.refetch();
			await refetchRef.current();
			setUnlockPending(false);
		})();
	}, [unlockPending]);

	/* Stable identity: `useVaultDocuments` keys its query on the joined ids, but
	   a fresh `[id]` literal every render still re-runs the effect that clears
	   the document selection. */
	const vaultIds = useMemo(() => [id], [id]);

	const onRenamed = useCallback(
		(next: IVaultDetail) => {
			setRenameOpen(false);
			applyVault(next);
			successNotification(`Vault renamed — ${next.name}`);
		},
		[applyVault, successNotification]
	);

	/* Archiving from HERE leaves the reader on a page that has left the tab they
	   came from, so it returns them to the drive (§8c). Restoring does not: the
	   vault in front of them is the one they wanted, and it is now active. */
	const onArchived = useCallback(
		(next: IVaultDetail, status: IVaultStatus) => {
			setArchiveOpen(false);
			if (status === 'archived') {
				successNotification(`Vault archived — ${next.name} is on the Archived tab`);
				navigate('/evault');
				return;
			}
			applyVault(next);
			successNotification(`Vault restored — ${next.name} is active again`);
		},
		[applyVault, navigate, successNotification]
	);

	/**
	 * The server's own breadcrumb (FR-V4), not one assembled here from the route.
	 * The first crumb links back to the drive; anything after it is the trail the
	 * server returned, so a nested vault would render correctly the day the
	 * backend grows one.
	 */
	const sub = useMemo(() => {
		const crumbs = vault?.breadcrumb?.length
			? vault.breadcrumb
			: [{ label: 'My eVault', vaultId: null }];
		const [root, ...rest] = crumbs;
		return (
			<>
				<Link className="vlink" to="/evault">
					{root?.label ?? 'My eVault'}
				</Link>
				{rest.map(crumb => (
					<span key={crumb.vaultId ?? crumb.label}>{` › ${crumb.label}`}</span>
				))}
			</>
		);
	}, [vault]);

	const actionTitle = canWrite ? undefined : NO_WRITE;

	return (
		<div className="sv2-scope sv2 evault-deck">
			<PageHead
				title={vault?.name ?? 'Vault'}
				sub={sub}
				action={
					state === 'ready' ? (
						<span className="hacts">
							<button
								className="dbtn"
								type="button"
								disabled={!canWrite}
								title={actionTitle}
								onClick={() => setRenameOpen(true)}
							>
								Rename
							</button>
							<button
								className="dbtn"
								type="button"
								disabled={!canWrite}
								title={actionTitle}
								onClick={() => setArchiveOpen(true)}
							>
								{archived ? 'Restore' : 'Archive'}
							</button>
						</span>
					) : undefined
				}
			/>

			{api.vaultTokenError ? (
				<div className="panel" style={{ marginTop: 12 }}>
					<div className="errbar">
						<span className="et">
							{api.vaultTokenError} The eVault service could not start a session.
						</span>
						<button
							className="reinv errbar__act"
							type="button"
							onClick={() => window.location.reload()}
						>
							Try again
						</button>
					</div>
				</div>
			) : state === 'loading' ? (
				<div className="panel" style={{ marginTop: 12 }}>
					<div className="sv2-loading">Loading vault…</div>
				</div>
			) : state === 'missing' ? (
				<div className="panel" style={{ marginTop: 12 }}>
					<Blank
						title="This vault isn't here"
						description="It may have been archived by its owner, or it belongs to someone else."
						action={
							<Link className="cta" to="/evault">
								Back to eVault
							</Link>
						}
					/>
				</div>
			) : state === 'error' ? (
				<div className="panel" style={{ marginTop: 12 }}>
					<div className="errbar" title={message}>
						<span className="et">
							Could not load this vault. The eVault service did not respond — this
							is a connection problem, not missing data.
						</span>
						<button
							className="reinv errbar__act"
							type="button"
							onClick={() => void refetch()}
						>
							Try again
						</button>
					</div>
				</div>
			) : entitlement.isLoaded && blocked ? (
				<div className="panel" style={{ marginTop: 12 }}>
					<Blank
						title="eVault is not on your plan yet"
						description="Add the eVault service to open this vault."
						action={
							<EvaultAddonToggle
								addon={addon}
								onChanged={unlock}
								canWrite={checkUserWritePermission(PERMISSION_SCREEN.Billing)}
							/>
						}
					/>
				</div>
			) : (
				<>
					{/* ONE STRIP, NOT FOUR CARDS.
					    These four facts describe ONE vault, and four separately
					    bordered cards with gaps between them read as four unrelated
					    things — the billing deck's head answers the same shape
					    (Month to date / Next billing / Plan / Payment method) as a
					    single panel with dividers, and this follows it. */}
					<div className="panel vstrip">
						<div className="vstrip__leg">
							<div className="l">Documents</div>
							<div className="n">{vault?.documentCount ?? 0}</div>
						</div>
						{/* A NAME, so sans and not tabular — the same opt-out billing's
						    Plan leg takes. */}
						<div className="vstrip__leg name">
							<div className="l">Owner</div>
							<div className="n" title={vault?.owner?.email}>
								{isYou(vault?.owner?._id) ? 'You' : vault?.owner?.name || '—'}
							</div>
						</div>
						<div className="vstrip__leg">
							<div className="l">Updated</div>
							<div className="n" title={isoOf(vault?.updatedAt)}>
								{toRelativeTime(vault?.updatedAt ?? '') || '—'}
							</div>
						</div>
						<div className="vstrip__leg chip">
							<div className="l">Encrypted</div>
							{/* The server's own claim and nothing more (C2): `encrypted` is
							    asserted by the vault record and the standard is shown as
							    its wording. Nothing in this stack measures encryption, so
							    nothing on this screen asserts it. */}
							<div className="n">
								{archived ? (
									<StatusChip verdict="ns">ARCHIVED</StatusChip>
								) : vault?.encrypted ? (
									<>
										<StatusChip verdict="ok">ENCRYPTED</StatusChip>
										{!!vault?.encryptionStandard && (
											<span className="estd">{vault.encryptionStandard}</span>
										)}
									</>
								) : (
									'—'
								)}
							</div>
						</div>
					</div>

					<DocumentPanel
						api={api}
						vaultIds={vaultIds}
						hideVaultColumn
						view={view}
						blocked={blocked}
						transferPrice={addon.status?.transferPrice ?? 0}
						/* Deliberately NOT passed: this page knows exactly one vault, so the
						   re-vault modal has nothing to choose between and draws no picker —
						   the record goes back where it came from, which is this vault. */
						extraControls={
							/* `aria-checked` + role=radio, not `aria-pressed`: that is the
							   pair `.seg` in surface.scss actually styles. */
							<div className="seg" role="radiogroup" aria-label="View">
								<button
									type="button"
									role="radio"
									aria-checked={view === 'list'}
									onClick={() => setView('list')}
								>
									&#8801; List
								</button>
								<button
									type="button"
									role="radio"
									aria-checked={view === 'grid'}
									onClick={() => setView('grid')}
								>
									&#9638; Grid
								</button>
							</div>
						}
						bulkBar={({ selectedDocs, clear, openTransfer }) => (
							<div className="bulkbar">
								<span>
									<b>{selectedDocs.length}</b>{' '}
									{selectedDocs.length === 1 ? 'document' : 'documents'} selected
								</span>
								<button
									className="dbtn pri"
									type="button"
									disabled={blocked || selectedDocs.length === 0}
									title={blocked ? NEEDS_ADDON_TRANSFER : undefined}
									onClick={openTransfer}
								>
									Transfer ownership ({selectedDocs.length})
								</button>
								<button className="reinv" type="button" onClick={clear}>
									Clear
								</button>
							</div>
						)}
					/>

					<div className="foot">
						Documents cannot be uploaded directly. Every original here carries a
						certificate of completion and a signing audit trail.
					</div>
				</>
			)}

			<CreateVaultModal
				api={api}
				open={renameOpen}
				mode="rename"
				vault={vault}
				onClose={() => setRenameOpen(false)}
				onRenamed={onRenamed}
			/>

			<ArchiveModal
				api={api}
				open={archiveOpen}
				vault={vault}
				mode={archived ? 'restore' : 'archive'}
				onClose={() => setArchiveOpen(false)}
				onDone={onArchived}
			/>
		</div>
	);
};

export default VaultDeck;
